/* eslint-disable react-hooks/exhaustive-deps */

import React, { createContext, useState, useEffect, useContext } from "react";
import * as signalR from "@microsoft/signalr";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { getAllPendingNotifications } from "../Redux/actions/vouchersactions/GetAllNottifications";
import { getallNotificationsNew } from "../Redux/actions/NotificationNew";
import { storage } from "../Utils/storageService";

const SignalRContext = createContext(null);

export const SignalRProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const [realTimeNotifications, setRealTimeNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const dispatch = useDispatch();

  const signalRHubUrl = `${process.env.REACT_APP_API_URL}notificationHub`;

  const initializeConnection = () => {
    try {
      const token = storage.getToken();

      if (!token) {
        console.warn("[SignalR] No authentication token found");
        return null;
      }

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(signalRHubUrl, {
          accessTokenFactory: () => token,
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 10000);
            // console.log(`[SignalR] Reconnecting in ${delay}ms...`);
            return delay;
          },
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      newConnection.onclose((error) => {
        // console.log("[SignalR] Connection closed", error);
        setIsConnected(false);
        setConnectionError(error?.message || "Connection closed");
      });

      newConnection.onreconnecting((error) => {
        // console.log("[SignalR] Reconnecting...", error);
        setIsConnected(false);
      });

      newConnection.onreconnected((connectionId) => {
        // console.log("[SignalR] Reconnected. Connection ID:", connectionId);
        setIsConnected(true);
        setConnectionError(null);

        dispatch(getAllPendingNotifications());
      });

      return newConnection;
    } catch (error) {
      console.error("[SignalR] Error creating connection:", error);
      return null;
    }
  };

  const startConnection = async () => {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      // console.log("[SignalR] Connection already established");
      return true;
    }

    const token = storage.getToken();
    if (!token) {
      console.warn("[SignalR] Cannot connect: No authentication token");
      return false;
    }

    try {
      const newConnection = initializeConnection();
      if (!newConnection) {
        return false;
      }

      newConnection.on("ReceiveNotification", (notification) => {
        const token = storage.getToken();
        if (!token) {
          // console.warn("[SignalR] Notification received while logged out. Ignoring.");
          return;
        }

        // console.log("[SignalR] Received notification:", notification);

        const notificationWithTimestamp = {
          ...notification,
          timestamp: new Date().toISOString(),
          isRealTime: true,
          isRead: false,
        };

        setRealTimeNotifications((prev) => [notificationWithTimestamp, ...prev]);

        showNotificationToast(notification);

        dispatch(getallNotificationsNew(1, 10));
      });

      // await newConnection.start();
      const connectionPromise = newConnection.start();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("SignalR connection timeout")), 5000),
      );
      await Promise.race([connectionPromise, timeoutPromise]);

      // console.log("[SignalR] Connection started successfully");
      setConnection(newConnection);
      setIsConnected(true);
      setConnectionError(null);

      return true;
    } catch (error) {
      // console.error("[SignalR] Failed to start connection:", error);
      setIsConnected(false);
      setConnectionError(error.message);
      return false;
    }
  };

  const stopConnection = async () => {
    if (connection) {
      try {
        connection.off("ReceiveNotification");
        await connection.stop();
        // console.log("[SignalR] Connection stopped");
      } catch (error) {
        console.error("[SignalR] Error stopping connection:", error);
      } finally {
        setIsConnected(false);
        setConnection(null);
        setRealTimeNotifications([]);
      }
    }
  };
  const showNotificationToast = (notification) => {
    const token = storage.getToken();
    if (!token) {
      return;
    }

    const { notificationId, message, referanceId, referanceType } = notification;

    const toastId = `notification-${notificationId || Date.now()}`;

    if (toast.isActive(toastId)) {
      return;
    }

    const truncateText = (text, maxLines = 4) => {
      if (!text) {
        return "";
      }
      const lines = text.split("\n");
      if (lines.length <= maxLines) {
        return text;
      }
      return `${lines.slice(0, maxLines).join("\n")}...`;
    };

    const truncatedMessage = truncateText(message, 4);

    toast.success(
      <div>
        <div
          style={{
            fontWeight: "bold",
            fontSize: "14px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.4,
            maxHeight: "calc(1.4em * 4)",
          }}
        >
          {truncatedMessage || "Notification"}
        </div>
        {referanceId && referanceType && (
          <div
            style={{
              fontSize: "12px",
              opacity: 0.9,
              marginTop: "4px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {referanceType}: {referanceId}
          </div>
        )}
      </div>,
      {
        toastId,
        position: "top-right",
        autoClose: 10000,
        style: {
          color: "white",
          padding: "12px",
          borderRadius: "8px",
          maxWidth: "400px",
        },
      },
    );
  };

  const clearRealTimeNotifications = () => {
    setRealTimeNotifications([]);
  };

  const markRealTimeNotificationAsRead = (notificationId) => {
    setRealTimeNotifications((prev) =>
      prev.map((noti) =>
        noti.notificationId === notificationId ? { ...noti, isRead: true } : noti,
      ),
    );
  };

  useEffect(() => {
    const token = storage.getToken();

    if (token) {
      startConnection();
    } else {
      console.warn("[SignalR] No auth token available, skipping connection");
    }

    return () => {
      stopConnection();
    };
  }, []);

  useEffect(() => {
    const checkTokenAndConnect = () => {
      const token = storage.getToken();
      if (token && !isConnected && !connection) {
        // console.log("[SignalR] Auth token found, attempting to connect...");
        startConnection();
      } else if (!token && (isConnected || connection)) {
        // console.log("[SignalR] No token found, stopping existing connection...");
        stopConnection();
      }
    };

    checkTokenAndConnect();

    const handleStorageChange = (e) => {
      if (e.key === "token") {
        checkTokenAndConnect();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isConnected, connection]);

  const value = {
    connection,
    isConnected,
    connectionError,
    realTimeNotifications,
    startConnection,
    stopConnection,
    clearRealTimeNotifications,
    markRealTimeNotificationAsRead,
    showNotificationToast,
  };

  return <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>;
};

SignalRProvider.propTypes = {
  children: PropTypes.node,
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }
  return context;
};

export default SignalRContext;
